import { useState, useEffect, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { useLingui } from "@lingui/react/macro";
import { Loader2, Trash2, UserMinus, PlusCircle } from "lucide-react";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { roleApi } from "@/lib/api/services/role.api";
import { userApi } from "@/lib/api/services/user.api";
import type { Role, PermissionGroup } from "@/lib/api/admin-api";
import type { RoleScopeAssignment } from "@/types/user";
import { AddScopeDialog } from "./AddScopeDialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const SCOPE_LABELS: Record<string, MessageDescriptor> = {
  ORGANIZATION: msg`Organization`,
  // The scope key stays COMPANY — the model rename is a separate change — but
  // the word users read is Concept (T-106).
  COMPANY: msg`Concept`,
  REGION: msg`Region`,
  LOCATION: msg`Location`,
};

type RoleApiSubset = Pick<typeof roleApi, 'update' | 'delete' | 'getUserAssignments' | 'getUsers'>;

interface RoleSheetProps {
  role: Role | null;
  onClose(): void;
  permissionGroups: PermissionGroup[];
  scopeAssignments: RoleScopeAssignment[];
  onRoleDeleted(): void;
  api?: RoleApiSubset;
  /** Query key base used for cache invalidation. Pass "org-roles" when using orgRoleApi. */
  queryKeyBase?: string;
  /** When true, permissions are shown but not editable and delete is hidden. */
  readOnly?: boolean;
}

function useScopeNameMap(scopeAssignments: RoleScopeAssignment[]) {
  const map = new Map<string, string>();
  for (const a of scopeAssignments) {
    map.set(`${a.scopeType}:${a.scopeId}`, a.scopeName);
  }
  return map;
}

export function RoleSheet({
  role,
  onClose,
  permissionGroups,
  scopeAssignments,
  onRoleDeleted,
  api: apiProp,
  queryKeyBase = "roles",
  readOnly = false,
}: RoleSheetProps) {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const api = apiProp ?? roleApi;

  const [syncedRoleId, setSyncedRoleId] = useState<string | undefined>(role?.id);
  const [localPermissions, setLocalPermissions] = useState<string[]>((role?.permissions ?? []) as string[]);
  const [isSaving, setIsSaving] = useState(false);
  const [addScopeOpen, setAddScopeOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [isDeleteCheckPending, setIsDeleteCheckPending] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingPermissionsRef = useRef<string[]>([]);

  if (role?.id !== syncedRoleId) {
    setSyncedRoleId(role?.id);
    setLocalPermissions((role?.permissions ?? []) as string[]);
    setIsSaving(false);
  }

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    pendingPermissionsRef.current = (role?.permissions ?? []) as string[];
  }, [role]);

  const scopeNameMap = useScopeNameMap(scopeAssignments);

  const saveMutation = useMutation({
    mutationFn: ({ roleId, permissions }: { roleId: string; permissions: string[] }) =>
      api.update(roleId, { permissions }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeyBase] });
      setIsSaving(false);
      toast.success(t`Saved`);
    },
    onError: (error: Error, variables) => {
      setIsSaving(false);
      const cachedRoles = queryClient.getQueryData<Role[]>([queryKeyBase]);
      const cachedRole = cachedRoles?.find((r) => r.id === variables.roleId);
      setLocalPermissions((cachedRole?.permissions ?? []) as string[]);
      toast.error(error.message || t`Failed to save permissions`);
    },
  });

  const togglePermission = (perm: string) => {
    const next = localPermissions.includes(perm)
      ? localPermissions.filter((p) => p !== perm)
      : [...localPermissions, perm];

    setLocalPermissions(next);
    pendingPermissionsRef.current = next;
    setIsSaving(true);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      saveMutation.mutate({ roleId: role!.id, permissions: pendingPermissionsRef.current });
    }, 300);
  };

  const { data: assignments = [], isLoading: assignmentsLoading } = useQuery({
    queryKey: [queryKeyBase, role?.id, "users"],
    queryFn: () => api.getUserAssignments(role!.id),
    enabled: !!role,
  });

  const revokeMutation = useMutation({
    mutationFn: ({ userId, userRoleId }: { userId: number; userRoleId: number }) =>
      userApi.removeScope(String(userId), userRoleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeyBase, role?.id, "users"] });
      queryClient.invalidateQueries({ queryKey: [queryKeyBase, "scopes"] });
      toast.success(t`Access revoked`);
    },
    onError: (error: Error) => {
      toast.error(error.message || t`Failed to revoke access`);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => api.delete(role!.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeyBase] });
      toast.success(t`Role deleted`);
      setConfirmDelete(false);
      onClose();
      onRoleDeleted();
    },
    onError: (error: Error) => {
      toast.error(error.message || t`Failed to delete role`);
    },
  });

  const handleDeleteClick = async () => {
    if (!role) return;
    setIsDeleteCheckPending(true);
    try {
      const users = await api.getUsers(role.id);
      if (users.length > 0) {
        toast.warning(t`Role cannot be deleted because it is assigned to users.`);
        return;
      }
      setConfirmDelete(true);
    } catch (error) {
      toast.error((error as Error).message || t`Failed to validate role`);
    } finally {
      setIsDeleteCheckPending(false);
    }
  };

  if (!role) return null;

  return (
    <>
      <Sheet open={!!role} onOpenChange={(open) => { if (!open) onClose(); }}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-lg p-0 flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-start justify-between p-5 border-b shrink-0">
            <div className="flex flex-col gap-1.5 pr-8">
              <SheetTitle className="text-xl font-semibold leading-tight">
                {role.name}
              </SheetTitle>
              <div className="flex items-center gap-2">
                <Badge variant="neutral" className="font-mono text-xs">
                  {role.code}
                </Badge>
                <Badge variant={readOnly ? "neutral" : "default"}>{readOnly ? t`Default` : t`Custom`}</Badge>
              </div>
            </div>
          </div>

          {/* Scrollable body */}
          <div className="flex-1 overflow-y-auto">
            {/* Permissions section */}
            <div className="p-5 border-b">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold">{t`Permissions`}</h3>
                <div className="flex items-center gap-2">
                  {isSaving && <Loader2 className="h-3.5 w-3.5 animate-spin text-(--color-text-secondary)" />}
                  <Badge variant="neutral">
                    {localPermissions.includes("*:*")
                      ? t`All`
                      : localPermissions.length}
                  </Badge>
                </div>
              </div>

              <div className="flex flex-col gap-5">
                {permissionGroups.map((group) => (
                  <div key={group.resource}>
                    <p className="text-xs font-semibold uppercase tracking-wider text-(--color-text-secondary) mb-2">
                      {group.resource}
                    </p>
                    <div className="flex flex-wrap gap-3">
                      {group.actions.map((action) => {
                        const perm = `${group.resource}:${action}`;
                        const id = `sheet-${perm}`;
                        const checked =
                          localPermissions.includes("*:*") ||
                          localPermissions.includes(`${group.resource}:*`) ||
                          localPermissions.includes(perm);
                        return (
                          <div key={perm} className="flex items-center gap-2">
                            <Switch
                              id={id}
                              disabled={readOnly || saveMutation.isPending}
                              checked={checked}
                              onCheckedChange={readOnly ? undefined : () => togglePermission(perm)}
                            />
                            <Label htmlFor={id} className={`text-sm font-mono ${readOnly ? "cursor-default" : "cursor-pointer"}`}>
                              {action}
                            </Label>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Scope assignments section */}
            <div className="p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold">{t`Scope Assignments`}</h3>
                {!readOnly && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-1.5"
                    onClick={() => setAddScopeOpen(true)}
                  >
                    <PlusCircle className="h-3.5 w-3.5" />
                    {t`Add`}
                  </Button>
                )}
              </div>

              {assignmentsLoading ? (
                <div className="flex items-center gap-2 py-8 justify-center text-sm text-(--color-text-secondary)">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t`Loading…`}
                </div>
              ) : assignments.length === 0 ? (
                <p className="text-sm text-(--color-text-secondary) py-6 text-center">
                  {t`No scope assignments for this role.`}
                </p>
              ) : (
                <div className="rounded-lg border overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-(--color-surface-muted) hover:bg-(--color-surface-muted)">
                        <TableHead className="h-auto px-3 py-2 text-left text-xs font-medium text-(--color-text-secondary)">
                          {t`User`}
                        </TableHead>
                        <TableHead className="h-auto px-3 py-2 text-left text-xs font-medium text-(--color-text-secondary)">
                          {t`Scope`}
                        </TableHead>
                        <TableHead className="h-auto px-3 py-2 text-right text-xs font-medium text-(--color-text-secondary)">
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {assignments.map((a) => {
                        const userName = [a.user.firstName, a.user.lastName]
                          .filter(Boolean)
                          .join(" ") || a.user.email;
                        const scopeName = a.scopeType && a.scopeId
                          ? scopeNameMap.get(`${a.scopeType}:${a.scopeId}`) ??
                            `${a.scopeType} #${a.scopeId}`
                          : t`Global`;

                        return (
                          <TableRow
                            key={a.id}
                            className="last:border-b-0 hover:bg-(--color-surface-muted)/50"
                          >
                            <TableCell className="px-3 py-2.5">
                              <div className="font-medium leading-tight">{userName}</div>
                              <div className="text-xs text-(--color-text-secondary)">{a.user.email}</div>
                            </TableCell>
                            <TableCell className="px-3 py-2.5">
                              {a.scopeType ? (
                                <div>
                                  <Badge variant="neutral" className="font-mono text-xs mb-0.5">
                                    {SCOPE_LABELS[a.scopeType] ? t(SCOPE_LABELS[a.scopeType]!) : a.scopeType}
                                  </Badge>
                                  <div className="text-xs text-(--color-text-secondary)">{scopeName}</div>
                                </div>
                              ) : (
                                <span className="text-xs text-(--color-text-secondary)">{t`Global`}</span>
                              )}
                            </TableCell>
                            <TableCell className="px-3 py-2.5 text-right">
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 w-7 p-0 text-(--color-text-secondary) hover:text-(--color-danger-text)"
                                disabled={revokeMutation.isPending}
                                onClick={() =>
                                  revokeMutation.mutate({
                                    userId: a.userId,
                                    userRoleId: a.id,
                                  })
                                }
                                aria-label={t`Revoke access`}
                              >
                                <UserMinus className="h-3.5 w-3.5" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          </div>

          {/* Footer — delete (hidden in read-only mode) */}
          {!readOnly && (
            <div className="p-5 border-t shrink-0">
              <Button
                variant="destructive"
                size="sm"
                disabled={isDeleteCheckPending || deleteMutation.isPending}
                onClick={handleDeleteClick}
                className="gap-2"
              >
                {isDeleteCheckPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
                {t`Delete role`}
              </Button>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Add scope dialog */}
      <AddScopeDialog
        role={role}
        open={addScopeOpen}
        onOpenChange={setAddScopeOpen}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ["roles", role.id, "users"] });
        }}
      />

      {/* Delete confirmation */}
      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t`Delete role?`}</AlertDialogTitle>
            <AlertDialogDescription>
              {t`This will permanently deactivate "${role.name}". This action cannot be undone.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>
              {t`Cancel`}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                deleteMutation.mutate();
              }}
              className="bg-(--color-danger) text-(--color-danger-fg) hover:bg-(--color-danger)/90"
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? t`Deleting…` : t`Delete role`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
